/**
 * Shared per-subtree error boundary. A render-time throw inside the
 * wrapped children is caught here → the user sees a recoverable card
 * while the rest of the dashboard (nav, top bar) keeps working, instead
 * of the whole SPA white-screening.
 *
 * Promoted out of `knowledge/lib/` so every route group (agents,
 * contacts, tables, tools, feedback, settings) — plus a root-level
 * safety net in `App.tsx` — can reuse the same fallback. The knowledge
 * module re-exports this from its old path for backwards compatibility.
 *
 * NFR-Design § 5: one per page route, mounted above `<Suspense>` + the
 * page. Copy lives under the `knowledge` i18n namespace (globally
 * registered + bundled), so the fallback renders the same everywhere.
 */

import { Alert, AlertDescription, AlertTitle, Button } from "@cometchat-ai/ui-dashboard";
import { Component, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

function ErrorCard(): ReactNode {
  const { t } = useTranslation("knowledge");
  return (
    <div className="mx-auto max-w-xl py-12">
      <Alert variant="destructive" role="alert">
        <AlertTitle>{t("errors.boundary.title")}</AlertTitle>
        <AlertDescription>
          <p>{t("errors.boundary.body")}</p>
          <Button className="mt-4" onClick={() => window.location.reload()}>
            {t("errors.boundary.reload")}
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}

export class PageErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    if (import.meta.env.MODE !== "production") {
      console.error("PageErrorBoundary caught:", error);
    }
  }

  override render(): ReactNode {
    return this.state.error ? <ErrorCard /> : this.props.children;
  }
}
